import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button,
  CircularProgress, Alert, IconButton, Stack, Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../contexts/AuthContext';
import RenewalPaymentFlow from './RenewalPaymentFlow';
import MobileSubscriptionInfo from './MobileSubscriptionInfo';
import { getPayableRenewalCases, PayableRenewalCase } from '../../services/companyBillingService';

interface LicensePaymentDialogProps {
  open: boolean;
  onClose: () => void;
}

const fmtMoney = (amount?: number, currency?: string) => {
  const n = typeof amount === 'number' ? amount : 0;
  return `${n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || 'TRY'}`;
};

const fmtDate = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const sourceLabel = (sourceType?: string) => {
  if (sourceType === 'FiscalDevice') return 'Yazarkasa';
  if (sourceType === 'Contract') return 'Sözleşme';
  return 'Panel / Lisans';
};

/**
 * Panel içi ödeme popup'ı (header'daki demo/lisans rozetine tıklanınca açılır).
 *
 * Açılınca GET /api/Company/payable-renewal-cases ile çağıran şirketin TÜM aktif
 * tahsilatlarını listeler (panel lisansı, sözleşme ve yazarkasa). "Öde" seçilince
 * ilgili token ile RenewalPaymentFlow çalışır.
 */
const LicensePaymentDialog: React.FC<LicensePaymentDialogProps> = ({ open, onClose }) => {
  const { refreshCompany } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cases, setCases] = useState<PayableRenewalCase[]>([]);
  const [activeToken, setActiveToken] = useState<string | null>(null);

  const loadCases = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const list = await getPayableRenewalCases();
      setCases(list);
    } catch {
      if (!silent) {
        setError('Ödeme bilgisi alınamadı. Lütfen tekrar deneyin.');
        setCases([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setActiveToken(null);
      loadCases();
    } else {
      setCases([]);
      setActiveToken(null);
      setError(null);
      setLoading(true);
    }
  }, [open, loadCases]);

  const handlePaid = useCallback(() => {
    void refreshCompany();
    loadCases(true);
  }, [refreshCompany, loadCases]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PaymentsIcon color="primary" />
          <span>Lisans / Ödeme</span>
        </Stack>
        <IconButton
          aria-label="Kapat"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <MobileSubscriptionInfo />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
        ) : activeToken ? (
          <Box>
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => setActiveToken(null)}
              sx={{ mb: 1 }}
            >
              Ödemelere dön
            </Button>
            <RenewalPaymentFlow token={activeToken} onPaid={handlePaid} onClose={onClose} />
          </Box>
        ) : cases.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Ödeme için sizi bekleyen bir tutar yok
            </Typography>
            <Typography color="text.secondary">
              Lisans satın alımı, yenileme veya cihaz ödemesi için henüz bir kayıt oluşturulmamış.
              Tutarınız tanımlandığında ödemenizi buradan güvenle tamamlayabilirsiniz.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5} sx={{ py: 1 }}>
            {cases.map((c) => {
              const productNames = c.lines?.map((l) => l.name).filter(Boolean) ?? [];
              return (
                <Box
                  key={c.token}
                  sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <Chip
                          size="small"
                          label={sourceLabel(c.sourceType)}
                          color={c.sourceType === 'FiscalDevice' ? 'warning' : 'default'}
                        />
                      </Stack>
                      <Typography sx={{ fontWeight: 600 }}>
                        {productNames.length > 0 ? productNames.join(', ') : sourceLabel(c.sourceType)}
                      </Typography>
                      {c.branchName && (
                        <Typography variant="body2" color="text.secondary">
                          Şube: {c.branchName}
                        </Typography>
                      )}
                      {fmtDate(c.expiryDate) && (
                        <Typography variant="body2" color="text.secondary">
                          Son tarih: {fmtDate(c.expiryDate)}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>
                        {fmtMoney(c.totalAmount, c.currency)}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<PaymentsIcon />}
                      onClick={() => setActiveToken(c.token)}
                    >
                      Öde
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LicensePaymentDialog;
